/**
 * ownershipTransfer.ts — WEB-1866.
 *
 * Общая точка для действия «владение проектом передано»: имя действия, форма
 * его содержимого и опознаватель. Читают её ДВА среза:
 *   - `projectMembersSlice` — заводит thunk с этим именем и правит состав
 *     команды и МОЮ роль;
 *   - `projectsSlice` — тем же ответом понижает мою роль в списке проектов
 *     (запасной источник роли для всего кабинета, useProjectRole).
 *
 * Почему отдельный файл, а не прямой импорт thunk'а срезом проектов: срез
 * участников тянет за собой клиент API (`@/api/projectMembers` → `@/api/client`
 * с `import.meta`), и прямой импорт добавил бы эту зависимость в срез
 * проектов, который поднимают больше сотни тестовых сторов по всему кабинету —
 * они падали бы на разборе `import.meta` в jest. Здесь нет ни одной
 * исполняемой зависимости: только строка, тип и функция.
 *
 * Имя действия задаётся ЗДЕСЬ и отсюда же берётся обеими сторонами — второй
 * копии строки в проекте быть не должно, иначе переименование thunk'а тихо
 * рассоединит половины правила.
 */
import type { ProjectMember } from '@/types';

/** Префикс thunk'а передачи владения (см. projectMembersSlice). */
export const TRANSFER_OWNERSHIP_ACTION_PREFIX =
  'projectMembers/transferOwnership';

/** Полное имя действия «передача удалась». */
export const TRANSFER_OWNERSHIP_FULFILLED = `${TRANSFER_OWNERSHIP_ACTION_PREFIX}/fulfilled`;

export interface TransferOwnershipFulfilledPayload {
  /** Строка НОВОГО владельца — ровно то, что вернул сервер. */
  member: ProjectMember;
  /** Проект, у которого сменился владелец. */
  projectId: string;
  /**
   * Действующее лицо (тот, кто нажал кнопку) этой передачей ПЕРЕСТАЛО быть
   * владельцем проекта. Считается один раз в thunk'е по строке участника и
   * роли в организации — см. докблок `didActorLoseOwnership`.
   */
  actorLostOwnership: boolean;
}

export interface TransferOwnershipFulfilledAction {
  type: typeof TRANSFER_OWNERSHIP_FULFILLED;
  payload: TransferOwnershipFulfilledPayload;
}

/** Опознаёт действие «передача удалась» в чужом срезе (без импорта thunk'а). */
export function isTransferOwnershipFulfilled(action: {
  type: string;
  payload?: unknown;
}): action is TransferOwnershipFulfilledAction {
  if (action.type !== TRANSFER_OWNERSHIP_FULFILLED) return false;
  const payload = action.payload as Partial<TransferOwnershipFulfilledPayload>;
  // Форму проверяем: срез проектов принимает решение по чужому содержимому.
  return (
    typeof payload?.projectId === 'string' &&
    typeof payload?.actorLostOwnership === 'boolean'
  );
}
